import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassSession, AttendanceRecord, AttendanceStatus } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

export interface SessionListQuery {
  classId?: string;
  dancerId?: string;
  from?: string;
  to?: string;
}

export function useClassSessions(query: SessionListQuery = {}) {
  return useQuery({
    queryKey: ['class-sessions', query],
    queryFn: async (): Promise<ClassSession[]> => {
      let q = supabase
        .from('class_sessions')
        .select('*')
        .order('session_date', { ascending: false });
      if (query.classId) q = q.eq('class_id', query.classId);
      if (query.from) q = q.gte('session_date', query.from);
      if (query.to) q = q.lte('session_date', query.to);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useGenerateSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (classId: string): Promise<number> => {
      const { data, error } = await supabase.rpc('generate_class_sessions', {
        p_class_id: classId,
      });
      if (error) throw error;
      return data ?? 0;
    },
    onSuccess: (_, classId) => {
      void qc.invalidateQueries({ queryKey: ['class-sessions'] });
      void qc.invalidateQueries({ queryKey: ['class-sessions', { classId }] });
    },
  });
}

export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; reason: string }) => {
      const { error } = await supabase
        .from('class_sessions')
        .update({ cancelled: true, cancellation_reason: args.reason })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['class-sessions'] });
    },
  });
}

// Attendance ---------------------------------------------------------------

export function useSessionAttendance(sessionId: string | undefined) {
  return useQuery({
    enabled: !!sessionId,
    queryKey: ['attendance', 'session', sessionId],
    queryFn: async (): Promise<AttendanceRecord[]> => {
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('class_session_id', sessionId!);
      if (error) throw error;
      return (data ?? []) as AttendanceRecord[];
    },
  });
}

// An attendance record joined to its class session's date. The bare
// attendance row has no session date of its own, so the parent UI previously
// showed `created_at` (the marked-on timestamp) mislabeled as the session
// date. We embed `class_sessions(session_date)` to surface the real date.
export interface DancerAttendanceRow extends AttendanceRecord {
  class_sessions: { session_date: string } | null;
}

export function useDancerAttendance(dancerId: string | undefined) {
  return useQuery({
    enabled: !!dancerId,
    queryKey: ['attendance', 'dancer', dancerId],
    queryFn: async (): Promise<DancerAttendanceRow[]> => {
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*, class_sessions(session_date)')
        .eq('dancer_id', dancerId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as DancerAttendanceRow[];
    },
  });
}

// Cache the current user's id once for the hook's lifetime — saves one
// auth round-trip per attendance write. The user id is stable for the
// session; logouts force a full nav so the cached value cannot leak.
let _cachedAttendanceMarkerId: string | undefined;
async function getMarkerUserId(): Promise<string | undefined> {
  if (_cachedAttendanceMarkerId) return _cachedAttendanceMarkerId;
  const userId = (await supabase.auth.getUser()).data.user?.id;
  _cachedAttendanceMarkerId = userId;
  return userId;
}

export function useMarkAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      class_session_id: string;
      dancer_id: string;
      status: AttendanceStatus;
      notes?: string;
    }): Promise<AttendanceRecord> => {
      const userId = await getMarkerUserId();
      // Upsert keyed by (class_session_id, dancer_id) — the unique index in
      // 0004 lets us swap status without an explicit two-step delete-then-insert.
      const { data, error } = await supabase
        .from('attendance_records')
        .upsert(
          stripUndefined({
            class_session_id: args.class_session_id,
            dancer_id: args.dancer_id,
            status: args.status,
            notes: args.notes,
            marked_by: userId,
          }),
          { onConflict: 'class_session_id,dancer_id' },
        )
        .select('*')
        .single();
      if (error) throw error;
      return data as AttendanceRecord;
    },
    onSuccess: (record) => {
      void qc.invalidateQueries({
        queryKey: ['attendance', 'session', record.class_session_id],
      });
      void qc.invalidateQueries({
        queryKey: ['attendance', 'dancer', record.dancer_id],
      });
    },
  });
}

// Schedule conflicts (server-side authoritative copy) ----------------------

export interface ScheduleConflictRow {
  kind: 'room' | 'instructor';
  weekday: string;
  class_a_id: string;
  class_a_name: string;
  class_b_id: string;
  class_b_name: string;
  shared_value: string;
}

export function useScheduleConflicts(termId: string | undefined) {
  return useQuery({
    enabled: !!termId,
    queryKey: ['conflicts', termId],
    queryFn: async (): Promise<ScheduleConflictRow[]> => {
      const { data, error } = await supabase.rpc('schedule_conflicts', {
        p_term_id: termId!,
      });
      if (error) throw error;
      return (data ?? []) as ScheduleConflictRow[];
    },
  });
}
